import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router";
import { useAppDispatch } from "hooks/redux";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";
import useModal from "hooks/modals/useModal";

import {
  GetTvView,
  PutTvView,
  TPutTvView,
  GetTvViewSlide,
  PostTvViewSlide,
  TPostTvViewSlide,
  PostTvViewHeaderImage,
  DeleteTvViewHeaderImage,
  DeleteTvView,
  DeleteTvViewSlide,
} from "api/rpc/2024-04/facilityAdmin/league/tvView/tvView";
import { GetLeagueCompetitionFormat } from "api/rpc/2024-04/facilityAdmin/league/scoring/format";
import { GetLeagueScoresheets } from "api/rpc/2024-04/facilityAdmin/league/scoring/scoresheet";
import { StatusCode } from "api/protocols";

import { showError, showSuccess } from "redux/actions/ui";
import { valueToString } from "helpers/Helpers";

import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import Toggle from "components/form/toggle/Toggle";
import { Select } from "components/select/index";
import CustomerImage from "elements/customer/CustomerImage";
import { ButtonNew as Button } from "components/buttonNew/index";

import { TLeagueTvView, TLeagueTvViewSlide, TLeagueRoundFormat, TLeagueScoresheet } from "redux/reducers/models/league";
import Popup from "components/popup/Popup";

export default function LeagueTvViewEdit() {
  const { leagueId, viewId } = useParams<{ leagueId: string; viewId: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const [view, setView] = useState<TLeagueTvView>(undefined);
  const [slides, setSlides] = useState<TLeagueTvViewSlide[]>(undefined);
  const [scoresheets, setScoresheets] = useState<TLeagueScoresheet[]>(undefined);
  const [formats, setFormats] = useState<TLeagueRoundFormat[]>(undefined);
  const [allFormats, setAllFormats] = useState<TLeagueRoundFormat[]>(undefined);

  const [deleteState, setDeleteState] = useState({
    deleteTvView: false,
    deleteTvViewSlide: false,
    slideToDelete: null,
  });

  const {
    state: newSlideModal,
    updateModal: updateNewSlideModal,
    closeModal: closeNewSlideModal,
  } = useModal<TPostTvViewSlide>({
    tv_view_id: viewId,
    type: "leaderboard",
    league_scoresheet_id: null,
    scoresheet_format_id: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTvView(source.token);
    void loadScoresheets(source.token);
    return () => source.cancel();
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (newSlideModal.league_scoresheet_id) {
      void loadFormats(source.token);
    }
    return () => source.cancel();
  }, [newSlideModal.league_scoresheet_id]);

  async function loadTvView(token?: CancelToken) {
    setSlides(undefined);

    const viewRes = await GetTvView({ id: viewId }, true, token);

    if (token && token.reason) {
      return;
    }
    if (viewRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading TV view")));
      return;
    }

    const slidesRes = await GetTvViewSlide({ tv_view_id: viewId }, true, token);

    if (slidesRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading TV view slides")));
      return;
    }

    setView(viewRes.data[0]);
    setSlides(slidesRes.data);
  }

  async function loadScoresheets(token?: CancelToken) {
    const scoresheetRes = await GetLeagueScoresheets({ league_id: leagueId }, false, token);

    if (token && token.reason) {
      return;
    }
    if (scoresheetRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league rounds")));
      return;
    }

    setScoresheets(scoresheetRes.data);

    const formatRes = await GetLeagueCompetitionFormat({ league_id: leagueId }, false, token);

    if (token && token.reason) {
      return;
    }
    if (formatRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league formats")));
      return;
    }

    setAllFormats(formatRes.data);
  }

  async function loadFormats(token?: CancelToken) {
    setFormats(undefined);

    const formatRes = await GetLeagueCompetitionFormat(
      { league_id: leagueId, league_scoresheet_id: newSlideModal.league_scoresheet_id },
      false,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (formatRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league formats")))
      return;
    }

    setFormats(formatRes.data);
  }

  async function updateTvView() {
    const params = {
      id: viewId,
      title: view.title,
      auto_play: view.auto_play,
      auto_play_interval: view.auto_play_interval,
    };

    const viewRes = await PutTvView(params, true);

    if (viewRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating TV view")));
      return;
    }

    dispatch(showSuccess(t("TV view updated successfully")));
    void loadTvView();
  }

  async function createSlide() {
    const slideRes = await PostTvViewSlide(
      {
        tv_view_id: viewId,
        type: newSlideModal.type,
        scoresheet_format_id: newSlideModal.scoresheet_format_id,
      },
      true,
    );

    if (slideRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating TV view slide")));
      return;
    }

    dispatch(showSuccess(t("TV view slide created successfully")));
    closeNewSlideModal();
    void loadTvView();
  }

  async function saveHeaderImage(imageFile: File, type: "left" | "right") {
    const formData = new FormData();

    formData.append("image", imageFile);
    formData.append("type", type);
    formData.append("id", viewId);

    const imageRes = await PostTvViewHeaderImage(formData, true);
    if (imageRes.status !== StatusCode.OK) {
      return;
    }

    void loadTvView();
  }

  async function deleteHeaderImage(type: "left" | "right") {
    const deleteRes = await DeleteTvViewHeaderImage({ type: type, id: viewId }, true);

    if (deleteRes.status !== StatusCode.OK) {
      return;
    }

    void loadTvView();
  }

  const primaryAction = {
    content: t("Save"),
    action: updateTvView,
  };

  const secondaryActions = [
    {
      content: t("View"),
      action: () => window.open(`/admin/league/${leagueId}/scoring/tv-views/${viewId}/view`),
    },
    {
      content: t("Delete"),
      action: () => setDeleteState(prevState => ({ ...prevState, deleteTvView: true })),
    },
  ];

  async function deleteTvView() {
    const deleteRes = await DeleteTvView({ id: viewId }, true);

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting TV view ")));
      return;
    }

    dispatch(showSuccess(t("TV View deleted successfully")));
    setDeleteState(prevState => ({ ...prevState, deleteTvView: false }));
    history.push(`/admin/league/${leagueId}/scoring/tv-views`);
  }

  async function deleteTvSlide() {
    const deleteRes = await DeleteTvViewSlide({ id: deleteState.slideToDelete.id }, true);

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting TV view slide")));
      return;
    }

    dispatch(showSuccess(t("TV View Slide deleted successfully")));
    setDeleteState(prevState => ({ ...prevState, deleteTvViewSlide: false }));
    void loadTvView();
  }

  return (
    <Page
      title={t("Edit TV View")}
      subtitle={view?.title}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Tv Views"),
          url: `/admin/league/${leagueId}/scoring/tv-views`,
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Title")}
                value={view?.title}
                onChange={e => setView({ ...view, title: e.target.value })}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Toggle
                checked={view?.auto_play}
                onChange={e =>
                  setView({
                    ...view,
                    auto_play: e.target.checked,
                    auto_play_interval: e.target.checked ? 10 : null,
                  })
                }
                label={t("Auto Play")}
                disabled={slides?.length <= 1}
              />
              {view?.auto_play ? (
                <Input
                  label={t("Interval")}
                  value={view?.auto_play_interval}
                  onChange={e => setView({ ...view, auto_play_interval: Number(e.target.value) })}
                  type={"number"}
                  suffix={t("Seconds")}
                />
              ) : null}
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
        <Card.Section title={t("Images")}>
          <div className="flex gap-4">
            <div className="stats_bar-picture">
              <span>{"Left Header"}</span>
              <CustomerImage
                imageSource={view?.header_image_left_source}
                saveProfileImage={imageFile => saveHeaderImage(imageFile, "left")}
                deleteProfileImage={() => deleteHeaderImage("left")}
              />
            </div>
            <div className="stats_bar-picture">
              <span>{"Right Header"}</span>
              <CustomerImage
                imageSource={view?.header_image_right}
                saveProfileImage={imageFile => saveHeaderImage(imageFile, "right")}
                deleteProfileImage={() => deleteHeaderImage("right")}
              />
            </div>
          </div>
        </Card.Section>
      </Card>
      <Card
        title={t("Slides")}
        titleActions={[
          {
            content: t("New Slide"),
            action: () => updateNewSlideModal({ isOpen: true }),
          },
        ]}
      >
        <Card.Section table>
          <DataTable
            columns={[
              { label: t("Round") },
              { label: t("Format") },
              { label: t("Type") },
              { label: "" },
            ]}
            loading={!slides}
          >
            {slides?.map((slide, index) => {
              const format = allFormats?.find(format => format.id === slide.scoresheet_format_id);
              return (
                <tr key={index}>
                  <td>{scoresheets?.find(scoresheet => scoresheet.id === format?.league_scoresheet_id)?.name}</td>
                  <td>{format?.title}</td>
                  <td>{valueToString(slide.type)}</td>
                  <td>
                    <Button
                      block
                      type="text"
                      size="small"
                      onClick={() =>
                        setDeleteState(prevState => ({ ...prevState, deleteTvViewSlide: true, slideToDelete: slide }))
                      }
                    >
                      {t("Delete")}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </Card.Section>
      </Card>

      <Portal isMounted={newSlideModal.isOpen}>
        <Sheet
          open={newSlideModal.isOpen}
          title={t("New Slide")}
          onOk={createSlide}
          okText={t("Create")}
          okDisabled={!newSlideModal.scoresheet_format_id}
          onCancel={closeNewSlideModal}
          size="small"
          overflow
        >
          <div className="mb-2">
            <Select
              label={t("Round")}
              defaultValue={newSlideModal.league_scoresheet_id}
              onChange={value => updateNewSlideModal({ league_scoresheet_id: value })}
            >
              {scoresheets?.map(scoresheet => (
                <Select.Option key={scoresheet.id} value={scoresheet.id}>
                  {scoresheet.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          <Select
            label={t("Format")}
            onChange={value => updateNewSlideModal({ scoresheet_format_id: value })}
            disabled={!newSlideModal.league_scoresheet_id}
            loading={formats === undefined}
          >
            {formats?.map(format => (
              <Select.Option key={format.id} value={format.id}>
                {format.title}
              </Select.Option>
            ))}
          </Select>
        </Sheet>
      </Portal>

      <Popup
        type="warning"
        open={deleteState.deleteTvView}
        title={t("Delete TV View")}
        description={t("Are you sure you want to delete this TV View? This cannot be undone.")}
        onOk={deleteTvView}
        onCancel={() => setDeleteState(prevState => ({ ...prevState, deleteTvView: false }))}
        okText={t("Delete")}
      />
      <Popup
        type="warning"
        open={deleteState.deleteTvViewSlide}
        title={t("Delete TV View Slide")}
        description={t("Are you sure you want to delete this TV View Slide? This cannot be undone.")}
        onOk={deleteTvSlide}
        onCancel={() => setDeleteState(prevState => ({ ...prevState, deleteTvViewSlide: false, slideToDelete: null }))}
        okText={t("Delete")}
      />
    </Page>
  );
}
